// frontend/src/widgets/QuoteDetailWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-27
// Updated:      2026-09-09 13:28 BST
//
// Description:
//   Quote detail + status widget.
//   - Loads quote via GET /api/quotes/{id}
//   - Loads status meta via GET /api/quotes/{id}/status/meta
//   - Applies governed transitions via POST /api/quotes/{id}/status
//   - Edits title/public_notes/internal_notes when meta.can_edit_quote=true
//   - Exposes admin override toggle only when meta.is_admin=true
//
// IMPORTANT:
//   Uses shared Axios instance ONLY.
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves quote loading, status metadata, PATCH save and governed status transitions.
//   - Core quote fields and totals collapse from three columns to one on mobile.
//   - Section headers wrap cleanly on narrow screens.
//   - Save / Cancel / Refresh controls become full-width touch targets on mobile.
//   - Admin override content stacks safely without overflowing.
//   - Allowed status-transition actions become a single-column mobile action list.
//   - Widget uses natural mobile height and retains the existing desktop minimum height.
//   - No API routes, payloads, permission logic, dirty checking or transition behaviour changed.

import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const cur = String(currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

const asText = (v) => {
  if (v === null || v === undefined) return "";
  return String(v);
};

const displayText = (v) => {
  const s = asText(v).trim();
  return s ? s : "—";
};

export default function QuoteDetailWidget({ quoteId, refreshKey, customerMap, onQuoteChanged }) {
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [quote, setQuote] = useState(null);
  const [meta, setMeta] = useState(null);

  const [override, setOverride] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);

  // Editable fields
  const [draftTitle, setDraftTitle] = useState("");
  const [draftPublicNotes, setDraftPublicNotes] = useState("");
  const [draftInternalNotes, setDraftInternalNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busySave, setBusySave] = useState(false);

  const card = useMemo(
    () => ({
      background: "#fff",
      borderRadius: 12,
      padding: 14,
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      minHeight: 0,
      boxSizing: "border-box",
      overflowWrap: "anywhere",
    }),
    []
  );

  const titleStyle = useMemo(
    () => ({
      fontWeight: 800,
      marginBottom: 10,
      fontFamily: theme?.font_secondary || theme?.font_primary || "inherit",
    }),
    [theme]
  );

  const label = useMemo(() => ({ fontSize: 12, color: "#6b7280", fontWeight: 800 }), []);
  const value = useMemo(() => ({ fontSize: 14, color: "#111827", fontWeight: 800 }), []);

  const input = useMemo(
    () => ({
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: "10px 10px",
      fontWeight: 800,
      outline: "none",
      fontFamily: theme?.font_primary || "inherit",
      color: "#111827",
      background: "#fff",
    }),
    [theme]
  );

  const textarea = useMemo(
    () => ({
      ...input,
      fontWeight: 700,
      minHeight: 96,
      resize: "vertical",
      whiteSpace: "pre-wrap",
    }),
    [input]
  );

  const buttonBase = useMemo(
    () => ({
      border: 0,
      borderRadius: 10,
      padding: "10px 12px",
      fontWeight: 800,
      cursor: "pointer",
      fontFamily: theme?.font_primary || "inherit",
    }),
    [theme]
  );

  const btnAction = (disabled) => ({
    ...buttonBase,
    background: disabled
      ? "#e5e7eb"
      : theme?.button_action?.normal || theme?.primary_color || "#111827",
    color: disabled ? "#6b7280" : "#fff",
    opacity: disabled ? 0.75 : 1,
  });

  const btnGeneral = (disabled) => ({
    ...buttonBase,
    background: disabled ? "#f3f4f6" : theme?.button_general?.normal || "#111827",
    color: disabled ? "#6b7280" : "#fff",
    opacity: disabled ? 0.75 : 1,
  });

  const sectionBox = useMemo(
    () => ({
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 12,
      display: "grid",
      gap: 10,
    }),
    []
  );

  const canEdit = Boolean(meta?.can_edit_quote);
  const allowedNext = meta?.allowed_next_statuses || [];

  const syncDraftsFromQuote = (q) => {
    setDraftTitle(asText(q?.title));
    setDraftPublicNotes(asText(q?.public_notes));
    setDraftInternalNotes(asText(q?.internal_notes));
    setDirty(false);
  };

  const load = async () => {
    if (!quoteId) {
      setQuote(null);
      setMeta(null);
      setErr("");
      setOverride(false);
      syncDraftsFromQuote(null);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const [qRes, mRes] = await Promise.all([
        api.get(`/api/quotes/${quoteId}`),
        api.get(`/api/quotes/${quoteId}/status/meta`),
      ]);
      const q = qRes.data || null;
      setQuote(q);
      setMeta(mRes.data || null);
      setOverride(false);
      syncDraftsFromQuote(q);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load quote");
      setQuote(null);
      setMeta(null);
      syncDraftsFromQuote(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, refreshKey]);

  const markDirty = () => {
    setDirty(true);
  };

  const resetDrafts = () => {
    syncDraftsFromQuote(quote);
  };

  const saveEdits = async () => {
    if (!quoteId || !canEdit) return;

    const patch = {
      title: draftTitle?.trim() ? draftTitle : null,
      public_notes: draftPublicNotes?.trim() ? draftPublicNotes : null,
      internal_notes: draftInternalNotes?.trim() ? draftInternalNotes : null,
    };

    // Avoid pointless calls
    const same =
      asText(quote?.title) === asText(patch.title) &&
      asText(quote?.public_notes) === asText(patch.public_notes) &&
      asText(quote?.internal_notes) === asText(patch.internal_notes);

    if (same) {
      setDirty(false);
      return;
    }

    setBusySave(true);
    setErr("");
    try {
      // Uses your existing PATCH endpoint
      await api.patch(`/api/quotes/${quoteId}`, patch);
      await load();
      onQuoteChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to save quote fields");
    } finally {
      setBusySave(false);
    }
  };

  const doTransition = async (toStatus) => {
    if (!quoteId || !meta) return;
    setBusyStatus(true);
    setErr("");
    try {
      await api.post(`/api/quotes/${quoteId}/status`, {
        to_status: toStatus,
        override: Boolean(override),
      });
      await load();
      onQuoteChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Status change failed");
    } finally {
      setBusyStatus(false);
    }
  };

  const custId = quote?.customer_id;
  const custName = customerMap?.[String(custId)] || (custId != null ? `Customer ${custId}` : "—");

  const editDisabled = !canEdit || busySave || busyStatus || loading;

  return (
    <div className="h-auto min-h-0 w-full overflow-visible md:min-h-[320px]" style={card}>
      <div style={titleStyle}>Quote Details</div>

      {!quoteId ? <div style={{ color: "#6b7280", fontWeight: 800 }}>Select a quote to view.</div> : null}

      {err ? (
        <div
          className="break-words"
          style={{
            color: "#b91c1c",
            fontWeight: 800,
            marginBottom: 10,
            overflowWrap: "anywhere",
          }}
        >
          {err}
        </div>
      ) : null}
      {loading ? <div style={{ fontWeight: 800 }}>Loading…</div> : null}

      {quote ? (
        <div style={{ display: "grid", gap: 12 }}>
          {/* Core fields */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <div style={label}>Quote</div>
              <div style={value}>#{quote.id}</div>
            </div>
            <div>
              <div style={label}>Customer</div>
              <div style={value}>{custName}</div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 800 }}>ID: {quote.customer_id}</div>
            </div>
            <div>
              <div style={label}>Status</div>
              <div style={value}>{displayText(quote.status)}</div>
            </div>

            <div>
              <div style={label}>Opportunity</div>
              <div style={value}>{quote.opportunity_id ?? "—"}</div>
            </div>
            <div>
              <div style={label}>Currency</div>
              <div style={value}>{displayText(quote.currency)}</div>
            </div>
            <div>
              <div style={label}>Can Edit</div>
              <div style={value}>{canEdit ? "Yes" : "No"}</div>
            </div>
          </div>

          {/* Editable: Title */}
          <div style={sectionBox}>
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-2.5">
              <div style={{ fontWeight: 900 }}>Title</div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 800 }}>
                {canEdit ? "Editable" : "Locked by status/access"}
              </div>
            </div>

            <input
              type="text"
              value={draftTitle}
              onChange={(e) => {
                setDraftTitle(e.target.value);
                markDirty();
              }}
              disabled={editDisabled}
              placeholder="Quote title"
              style={input}
            />
          </div>

          {/* Totals (server-calculated) */}
          <div style={sectionBox}>
            <div style={{ fontWeight: 900 }}>Totals</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <div style={label}>Subtotal</div>
                <div style={value}>{formatMoney(quote.subtotal, quote.currency)}</div>
              </div>
              <div>
                <div style={label}>Tax</div>
                <div style={value}>{formatMoney(quote.tax_total, quote.currency)}</div>
              </div>
              <div>
                <div style={label}>Total</div>
                <div style={{ ...value, fontSize: 16 }}>{formatMoney(quote.total, quote.currency)}</div>
              </div>
            </div>
          </div>

          {/* Editable: Notes */}
          <div style={sectionBox}>
            <div style={{ fontWeight: 900 }}>Notes</div>

            <div>
              <div style={label}>Public Notes</div>
              <textarea
                value={draftPublicNotes}
                onChange={(e) => {
                  setDraftPublicNotes(e.target.value);
                  markDirty();
                }}
                disabled={editDisabled}
                placeholder="Visible to customer / proposal content"
                style={textarea}
              />
            </div>

            <div>
              <div style={label}>Internal Notes</div>
              <textarea
                value={draftInternalNotes}
                onChange={(e) => {
                  setDraftInternalNotes(e.target.value);
                  markDirty();
                }}
                disabled={editDisabled}
                placeholder="Internal only (sales/admin notes)"
                style={textarea}
              />
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
            <button
              type="button"
              className="min-h-[44px] w-full sm:w-auto"
              onClick={saveEdits}
              disabled={!canEdit || busySave || !dirty}
              style={btnAction(!canEdit || busySave || !dirty)}
              title={!canEdit ? "You do not have edit access or quote is locked" : !dirty ? "No changes" : "Save"}
            >
              {busySave ? "Saving…" : "Save"}
            </button>

            <button
              type="button"
              className="min-h-[44px] w-full sm:w-auto"
              onClick={resetDrafts}
              disabled={busySave || !dirty}
              style={btnGeneral(busySave || !dirty)}
              title={!dirty ? "No changes" : "Discard changes"}
            >
              Cancel
            </button>

            <button
              type="button"
              className="min-h-[44px] w-full sm:w-auto"
              onClick={load}
              disabled={busySave || busyStatus}
              style={btnGeneral(busySave || busyStatus)}
            >
              Refresh
            </button>

            <div
              className="w-full break-words sm:w-auto"
              style={{
                fontSize: 12,
                color: "#6b7280",
                fontWeight: 800,
                overflowWrap: "anywhere",
              }}
            >
              Line items editable: {meta?.line_items_editable ? "Yes (draft/submitted)" : "No (locked)"}
            </div>
          </div>

          {/* Admin override */}
          {meta?.is_admin ? (
            <label className="mt-1 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-2.5">
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                disabled={!canEdit}
              />
              <span style={{ fontWeight: 900, color: "#111827" }}>Admin override mode</span>
              <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 800 }}>(only use for maintenance)</span>
            </label>
          ) : null}

          {/* Status transitions */}
          <div className="mt-1 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
            {allowedNext.map((s) => (
              <button
                key={s}
                type="button"
                className="min-h-[44px] w-full sm:w-auto"
                disabled={!canEdit || busyStatus}
                onClick={() => doTransition(s)}
                style={btnAction(!canEdit || busyStatus)}
              >
                Move to {s}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
